import React, { useState } from "react";
import { ChevronRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SPIRITUALITY_OPTIONS = [
  { id: "spiritual_not_religious", label: "Spiritual, not religious", icon: "🌟", description: "I have my own spiritual practices" },
  { id: "higher_power", label: "Believe in a Higher Power", icon: "🙏", description: "Central to 12-step programs" },
  { id: "christian", label: "Christian", icon: "✝️", description: "Faith-based recovery" },
  { id: "buddhist", label: "Buddhist", icon: "☸️", description: "Mindfulness and meditation" },
  { id: "agnostic", label: "Agnostic", icon: "🤔", description: "Open to possibilities" },
  { id: "atheist", label: "Atheist", icon: "🔬", description: "Secular recovery" },
  { id: "other", label: "Other", icon: "🌈", description: "My own path" },
  { id: "prefer_not_to_say", label: "Prefer not to say", icon: "🔒", description: "Private" },
];

const SpiritualityStep = ({ onNext }) => {
  const [spirituality, setSpirituality] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!spirituality) {
      toast.error("Please select an option");
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.submitSpirituality({
        spirituality: spirituality
      });
      onNext(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6 max-h-[80vh] overflow-y-auto">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-6 h-6 text-[#FAB710]" />
        <h2 className="text-xl font-bold text-gray-900">Spirituality</h2>
      </div>
      <p className="text-gray-600 mb-4">This helps with program compatibility, especially for 12-step based recovery</p>

      <div className="bg-blue-50 rounded-xl p-4 mb-6">
        <p className="text-sm text-blue-700">
          Many recovery programs involve spirituality or a "Higher Power" concept. This helps us match you with compatible partners.
        </p>
      </div>

      <div className="space-y-3">
        {SPIRITUALITY_OPTIONS.map(option => (
          <button
            key={option.id}
            onClick={() => setSpirituality(option.id)}
            className={`w-full px-4 py-4 rounded-xl border-2 text-left transition-all ${
              spirituality === option.id
                ? 'border-[#1C3A7A] bg-[#1C3A7A]/5'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">{option.icon}</span>
              <div>
                <p className={`font-medium ${spirituality === option.id ? 'text-[#1C3A7A]' : 'text-gray-900'}`}>
                  {option.label}
                </p>
                <p className="text-gray-500 text-sm">{option.description}</p>
              </div>
            </div>
          </button>
        ))}
      </div>

      <Button
        onClick={handleSubmit}
        disabled={submitting || !spirituality}
        className="w-full h-12 bg-[#1C3A7A] hover:bg-[#15305f] text-white font-semibold rounded-xl mt-6"
      >
        {submitting ? (
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <>
            Continue
            <ChevronRight className="w-5 h-5 ml-2" />
          </>
        )}
      </Button>
    </div>
  );
};

export default SpiritualityStep;
